import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FaceTrackerService } from '../../../face-tracker';
import { AABB } from '../../../geometry';
import { Subscription } from 'rxjs';
import { OverlayStatus } from '../../overlay/overlay-status.enum';
import { VideoComponent, VideoStatus } from '../../video';
import { OverlayComponent } from '../../../components/overlay/overlay.component';
import { FileService } from '@shared/services/file';
import { BrowserCompatibilityService } from '@shared/services/browser-compatibility';
import { MediaRecorderComponent } from '../../../../media-recorder/media-recorder.component';
import { StoreService } from '@shared/store';
import { selectDebugSettings } from '@shared/store/slices/debug';

enum UiStatus {
  Idle = 'idle',
  NeedsStart = 'needs-start',
  Started = 'started',
  Finished = 'finished',
}

const PREFIX = '[Check UI]';

@Component({
  selector: 'app-check',
  templateUrl: './check.component.html',
  styleUrls: ['./check.component.scss'],
  standalone: false,
})
export class CheckComponent implements OnChanges, OnDestroy, AfterViewInit {
  private readonly faceTrackerService = inject(FaceTrackerService);
  private readonly store = inject(StoreService);
  private readonly fileService = inject(FileService);
  private readonly browserCompatibilityService = inject(BrowserCompatibilityService);

  public readonly VideoStatus = VideoStatus;
  public readonly UiStatus = UiStatus;

  static readonly VIDEO_FRAME_INTERVAL = 250;
  private static readonly VIDEO_FRAMES_NEEDED = 12;
  private static readonly FACE_DETECTION_TIMEOUT_LONG = 4000;
  private static readonly FACE_DETECTION_TIMEOUT_SHORT = 1500;

  // TODO: This is style info and should be in custom CSS properties
  private static readonly ROI_MARGIN_TOP = 30;
  private static readonly ROI_MARGIN_BOTTOM = 125;
  private static readonly ROI_SAFETY_INSET = 30;
  private static readonly ROI_ASPECT = 238 / 305;

  @ViewChild('recorder', { static: true }) recorder!: MediaRecorderComponent;
  @ViewChild('buttonArea', { static: true }) buttonArea!: ElementRef<HTMLDivElement>;
  @ViewChild('centerPaddingArea', { static: true }) centerPaddingArea!: ElementRef<HTMLDivElement>;
  @ViewChild('guides', { static: true }) guides!: ElementRef<HTMLDivElement>;
  @ViewChild('pageContent', { static: true }) pageContent!: ElementRef<HTMLDivElement>;
  @ViewChild('overlay', { static: true }) overlay!: OverlayComponent;
  @ViewChild('captureCanvas', { static: true }) captureCanvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() video: VideoComponent | undefined;
  @Input() stream: MediaStream | undefined;

  /** A video file is ready. */
  @Output() fileReady = new EventEmitter<Blob>();
  /** A request to change video source. */
  @Output() changeMedia = new EventEmitter<void>();
  /**
   * A request for an initial video source.
   * This will be emitted when the page starts here, and we need to tap to start.
   */
  @Output() requestVideo = new EventEmitter<void>();

  public readonly OverlayStatus = OverlayStatus;
  public readonly CheckUiStatus = VideoStatus;
  public get IS_DESKTOP() {
    return this.browserCompatibilityService.isDesktop();
  }

  private faceDetectedSubscription: Subscription | undefined;
  private lastDetectionTimeMs = 0;
  private roi = new AABB(0, 0, 1, 1);
  private get captureCanvas() {
    return this.captureCanvasRef.nativeElement;
  }
  private interval: NodeJS.Timeout | undefined;
  private currentFrame: ImageData | undefined;
  private numFrames = 0;
  private tracking = false;
  public status = UiStatus.Idle;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stream && changes.stream.currentValue) {
      console.debug(PREFIX, 'Stream change detected. Already tracking?:', this.tracking);
      this.status = UiStatus.NeedsStart;
      if (this.tracking) {
        this.cancelRecordingAndTracking();
      }
      if (!this.IS_DESKTOP) {
        this.startTracking();
      }
    }
  }

  ngAfterViewInit(): void {
    this.updateRoi();
  }

  ngOnDestroy(): void {
    if (this.faceDetectedSubscription) {
      this.faceDetectedSubscription.unsubscribe();
    }
    this.cancelRecordingAndTracking();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateRoi();
  }

  /**
   * Set up the handler for face detection events
   */
  private createFaceDetectionHander(): void {
    this.faceDetectedSubscription = this.faceTrackerService.facesDetected.subscribe((next: number) => {
      this.handleDetection(next);
    });
  }

  public async pageClicked(): Promise<void> {
    if (!this.stream) {
      console.debug(PREFIX, 'Asking FaceCheckComponent to activate webcam.');
      this.requestVideo.emit();
    }
  }

  public handleChangeMedia() {
    this.changeMedia.emit();
  }

  public videoObtained() {
    console.debug(PREFIX, 'FaceCheckComponent says video is obtained.');
    this.status = UiStatus.NeedsStart;
    if (!this.IS_DESKTOP) {
      this.startTracking();
    }
  }

  updateRoi(): void {
    // Recalculate the ROI
    this.roi.x = 0;
    const offsetTop = 63;
    this.roi.y = offsetTop + CheckComponent.ROI_MARGIN_TOP;
    this.roi.width = this.pageContent.nativeElement.scrollWidth;
    this.roi.height = this.pageContent.nativeElement.scrollHeight - offsetTop - CheckComponent.ROI_MARGIN_BOTTOM;
    const aspect = this.roi.width / this.roi.height;
    let w = 0,
      h = 0;
    if (aspect < CheckComponent.ROI_ASPECT) {
      // i.e. if taller than we want...
      w = this.roi.width;
      h = w / CheckComponent.ROI_ASPECT; // ... then constrain height to available width
    } else {
      h = this.roi.height;
      w = h * CheckComponent.ROI_ASPECT;
    }
    this.roi = AABB.fromCenter(this.roi.centerX, this.roi.centerY, w, h);

    // Update the overlay's ROI (give it an inset)
    const roiInset = new AABB(0, 0, 0, 0);
    roiInset.x = this.roi.x + CheckComponent.ROI_SAFETY_INSET;
    roiInset.y = this.roi.y + CheckComponent.ROI_SAFETY_INSET;
    roiInset.width = this.roi.width - CheckComponent.ROI_SAFETY_INSET * 2;
    roiInset.height = this.roi.height - CheckComponent.ROI_SAFETY_INSET * 2;
    this.overlay.roi = roiInset;
    this.overlay.onResize();
  }

  //#region Detection
  public startTracking(): void {
    if (!this.stream) {
      console.error('Tried to start tracking, but no stream exists. Aborting.');
      return;
    }

    this.tracking = true;

    this.createFaceDetectionHander();
    this.overlay.status = OverlayStatus.Initial;
    this.status = UiStatus.Started;
    this.recorder.startRecording(this.stream);
    this.lastDetectionTimeMs = Date.now();
    this.numFrames = 0;
    this.faceTrackerService.start();
    if (!this.interval) {
      this.interval = setInterval(() => {
        this.captureFrame();
      }, CheckComponent.VIDEO_FRAME_INTERVAL);
    }
  }

  /** Cancel and drop the current recording and tracking information. */
  private cancelRecordingAndTracking(): void {
    console.debug(PREFIX, 'Cancelling recording/tracking');
    this.stopTracking();
    this.recorder.stopRecording().finally(() => {
      console.debug(PREFIX, 'Media from cancelling/recording dropped');
    });
  }

  private stopTracking(): void {
    this.faceTrackerService.stop();
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
    this.tracking = false;
  }

  private captureFrame(): void {
    const ctx = this.captureCanvas.getContext('2d', { willReadFrequently: true });

    if (!this.video) {
      throw Error('Cannot capture frame. No video present.');
    }
    if (!ctx) {
      throw Error('Cannot capture frame. No context2D available.');
    }

    const { width: videoWidth, height: videoHeight } = this.video.videoDimensions;
    const { width: windowWidth, height: windowHeight } = this.video.scrollDimensions;
    const videoAspect = videoWidth / videoHeight;
    const windowAspect = windowWidth / windowHeight;
    const targetWidth = videoAspect < windowAspect ? windowWidth : windowHeight * videoAspect;
    const targetHeight = videoAspect < windowAspect ? windowWidth / videoAspect : windowHeight;

    // Calculate the scale from screen pixels to video pixels
    const screenToVideoScaleX = videoWidth / targetWidth;
    const screenToVideoScaleY = videoHeight / targetHeight;

    // Calculate the visible portion of video, in video pixels (not screen pixels).
    const offsetX = Math.floor(((targetWidth - windowWidth) / 2) * screenToVideoScaleX);
    const offsetY = Math.floor(((targetHeight - windowHeight) / 2) * screenToVideoScaleY);
    const visibleWidth = Math.ceil(windowWidth * screenToVideoScaleX);
    const visibleHeight = Math.ceil(windowHeight * screenToVideoScaleY);

    this.captureCanvas.width = visibleWidth;
    this.captureCanvas.height = visibleHeight;
    ctx.drawImage(this.video.actual, offsetX, offsetY, visibleWidth, visibleHeight, 0, 0, visibleWidth, visibleHeight);
    this.currentFrame = ctx.getImageData(0, 0, visibleWidth, visibleHeight);

    // Scale the ROI from screen pixels to video pixels
    const roiVideo = new AABB(
      this.roi.x * screenToVideoScaleX,
      this.roi.y * screenToVideoScaleY,
      this.roi.width * screenToVideoScaleX,
      this.roi.height * screenToVideoScaleY,
    );

    // Send the frame to the face detector
    this.faceTrackerService.submitFrame(this.currentFrame, roiVideo);
  }

  private async handleDetection(count: number) {
    // Handle video completion here so that overlay animation gets to fully complete

    if (this.numFrames === CheckComponent.VIDEO_FRAMES_NEEDED) {
      this.numFrames++;
      this.stopTracking();
      const file = await this.recorder.stopRecording();
      if (this.debugDownloadVideo) {
        this.fileService.downloadToDevice(file, 'sample.webm');
      }
      this.fileReady.emit(file);
    }
    if (this.numFrames > CheckComponent.VIDEO_FRAMES_NEEDED) {
      return;
    }

    const faceDetected = count > 0;

    // If no face detected for a few seconds then let it through anyway, just in case
    // the face detector is failing (bad light, beards, glare, glasses, etc). Let a
    // human operator sort it out.
    if (!faceDetected) {
      let timeout = CheckComponent.FACE_DETECTION_TIMEOUT_SHORT;
      if (0 === this.numFrames) {
        timeout = CheckComponent.FACE_DETECTION_TIMEOUT_LONG;
      }
      if (Date.now() - this.lastDetectionTimeMs < timeout) {
        return;
      }
    }

    // Update time of last successful face detection
    this.lastDetectionTimeMs = Date.now();
    this.overlay.status = faceDetected ? OverlayStatus.FaceDetected : OverlayStatus.Initial;

    // Add successful face-detection frames to the video recording
    if (this.numFrames < CheckComponent.VIDEO_FRAMES_NEEDED) {
      this.numFrames++;
      this.overlay.setProgressAmount(this.numFrames / CheckComponent.VIDEO_FRAMES_NEEDED);
    }
  }
  //#endregion Detection

  private get debugDownloadVideo(): boolean {
    const debug = this.store.select(selectDebugSettings());
    return debug.enabled && debug.downloadMedia;
  }
}
